import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Calendar, FileText, Mail, Megaphone, ShieldAlert, BarChart3,
  Tag, Star, Inbox, Headphones, ArrowRight, Sparkles, ExternalLink, Bell,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ADMIN_TILES = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/admin', color: 'from-[#002249] to-[#0a3570]', roles: ['super_admin', 'membership_lead', 'membership_support', 'marketing_team', 'events_manager', 'blog_reviewer'] },
  { key: 'members', label: 'Members', icon: Users, path: '/admin/members', color: 'from-blue-600 to-blue-800', roles: ['super_admin', 'membership_lead', 'membership_support'] },
  { key: 'events', label: 'Events', icon: Calendar, path: '/admin/events', color: 'from-emerald-600 to-emerald-800', roles: ['super_admin', 'events_manager'] },
  { key: 'blog', label: 'Blog & News', icon: FileText, path: '/admin/blog', color: 'from-amber-600 to-amber-800', roles: ['super_admin', 'marketing_team', 'blog_reviewer'] },
  { key: 'newsletter', label: 'Newsletter', icon: Mail, path: '/admin/newsletter-center', color: 'from-rose-600 to-rose-800', roles: ['super_admin', 'events_manager', 'membership_lead', 'membership_support', 'marketing_team', 'blog_reviewer'] },
  { key: 'comms', label: 'Email AI', icon: Sparkles, path: '/admin/email-communications', color: 'from-purple-600 to-purple-800', roles: ['super_admin', 'marketing_team', 'membership_lead'] },
  { key: 'announcements', label: 'Announcements', icon: Megaphone, path: '/admin/announcements', color: 'from-orange-600 to-orange-800', roles: ['super_admin', 'marketing_team', 'membership_lead'] },
  { key: 'deals', label: 'Member Deals', icon: Tag, path: '/admin/member-deals', color: 'from-pink-600 to-pink-800', roles: ['super_admin', 'marketing_team', 'membership_lead'] },
  { key: 'testimonials', label: 'Testimonials', icon: Star, path: '/admin/testimonials', color: 'from-indigo-600 to-indigo-800', roles: ['super_admin', 'marketing_team', 'membership_lead'] },
  { key: 'requests', label: 'Marketing Requests', icon: Inbox, path: '/admin/marketing-requests', color: 'from-teal-600 to-teal-800', roles: ['super_admin', 'marketing_team'] },
  { key: 'support', label: 'Support Inbox', icon: Headphones, path: '/admin/support-inbox', color: 'from-cyan-600 to-cyan-800', roles: ['super_admin', 'membership_lead', 'membership_support'] },
];

const AppAdminPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const tiles = ADMIN_TILES.filter((t) => t.roles.includes(role));

  if (!tiles.length) {
    return (
      <div className="px-4 py-12 text-center" data-testid="admin-no-access">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 mx-auto mb-3 flex items-center justify-center">
          <ShieldAlert className="w-7 h-7 text-rose-500" />
        </div>
        <h1 className="text-[16px] font-bold text-[#002249]">No admin access</h1>
        <p className="text-[12.5px] text-slate-500 mt-1.5">This area is reserved for DCCF leadership and staff.</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-2" data-testid="app-admin">
      <div className="mb-4">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">Admin</p>
        <h1 className="text-[20px] font-bold text-[#002249] tracking-tight">Operations</h1>
        <p className="text-[12.5px] text-slate-500 mt-1">Quick access to your admin tools, optimized for mobile.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        {tiles.map((t, i) => (
          <motion.div
            key={t.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.03, 0.18) }}
          >
            <Link
              to={t.path}
              className="block bg-white rounded-2xl border border-slate-200 hover:border-[#002249]/25 hover:shadow-md transition-all p-4 active:scale-[0.98]"
              data-testid={`admin-tile-${t.key}`}
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center mb-3 shadow-sm`}>
                <t.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-[13.5px] font-bold text-[#002249] leading-tight">{t.label}</p>
              <p className="text-[11px] text-slate-400 mt-1.5 inline-flex items-center gap-1">
                Open <ArrowRight className="w-3 h-3" />
              </p>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="admin-desktop-hint">
        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <ExternalLink className="w-4 h-4 text-amber-700" />
        </div>
        <div className="min-w-0">
          <p className="text-[12.5px] font-bold text-[#002249]">Need full controls?</p>
          <p className="text-[11.5px] text-slate-600 mt-0.5 leading-relaxed">The full admin desktop dashboard offers tables, bulk actions, and advanced filters. The links above open inside the app — switch to a larger screen for heavy editing.</p>
        </div>
      </div>
    </div>
  );
};

export default AppAdminPage;
